import type { Plugin } from "chart.js";

type CrosshairOptions = { x: number; y: number; draw: boolean };

export const Crosshair: Plugin<"scatter", CrosshairOptions> = {
  // a unique identifier for the plugin
  id: "crosshair",
  // called after the chart has been initialized
  afterInit: (_chart, _args, crosshair) => {
    // initialize the crosshair plugin options
    crosshair.x = 0;
    crosshair.y = 0;
    crosshair.draw = false;
  },
  beforeEvent: (_chart, args) => {
    if (!args.event) {
      return;
    }
    const event = args.event;
    if (event.type === "mouseout") {
      window.dispatchEvent(new CustomEvent("activePointUnHover"));
    }
  },
  // called after a mouse event on the chart
  afterEvent: (chart, args, crosshair) => {
    if (!args.event || !chart.chartArea) {
      return;
    }

    const { x, y } = args.event;
    const { top, bottom, left, right } = chart.chartArea;

    crosshair.draw =
      x != null &&
      y != null &&
      x >= left &&
      x <= right &&
      y >= top &&
      y <= bottom;

    const activePoint = chart.tooltip?.dataPoints?.[0]?.element as
      | (NonNullable<typeof chart.tooltip>["dataPoints"][0]["element"] & {
          $context?: { raw?: Record<string, unknown> };
        })
      | undefined;

    const raw = activePoint?.$context?.raw;

    if (activePoint && raw) {
      crosshair.x = activePoint.x;
      crosshair.y = activePoint.y;

      window.dispatchEvent(
        new CustomEvent("activePoint", {
          detail: { ...raw },
        }),
      );
    }

    chart.draw();
  },
  // called after all datasets have been drawn on the chart
  afterDatasetsDraw: (chart, _args, crosshair) => {
    const { draw, x, y } = crosshair;

    // return early if the crosshair should not be drawn
    if (!draw) {
      return;
    }

    // define some styling options for the crosshair
    const lineColor = "#9CA3AF";
    const dotColor = "#9CA3AF";
    const dotRadius = 4;
    const lineDash = [4, 2];

    // get the canvas context and chart area
    const ctx = chart.ctx;

    const { top, bottom } = chart.chartArea;

    // draw the vertical line of the crosshair
    ctx.lineWidth = 1;
    ctx.strokeStyle = lineColor;
    ctx.setLineDash(lineDash);
    ctx.beginPath();
    ctx.moveTo(x, bottom);
    ctx.lineTo(x, top);
    ctx.stroke();

    // draw the dot of the crosshair
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(x, y, dotRadius, 0, 2 * Math.PI);
    ctx.fillStyle = dotColor;
    ctx.fill();
  },
};

export default Crosshair;
